import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import AiSettingsAdmin from './ai/AiSettingsAdmin';
import DocTemplatesAdmin from './docs/DocTemplatesAdmin';
import './AdminPanel.css';

const AdminPanel = ({ currentUser }) => {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [query, setQuery] = useState('');
  const [newPasswordByUser, setNewPasswordByUser] = useState({});
  const [recoveryEmailByUser, setRecoveryEmailByUser] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupMemberInput, setGroupMemberInput] = useState('');
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [groupModalError, setGroupModalError] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
  const creatorEmail = normalizeEmail(editingGroup?.createdBy?.email);

  const loadAdminData = async (search = query) => {
    setError('');
    const [summaryRes, usersRes, logsRes, groupsRes, feedbackRes] = await Promise.allSettled([
      axiosInstance.get('/api/admin/summary'),
      axiosInstance.get('/api/admin/users', { params: { q: search } }),
      axiosInstance.get('/api/admin/logs', { params: { lines: 40 } }),
      axiosInstance.get('/api/admin/groups'),
      axiosInstance.get('/api/feedback'),
    ]);

    if (summaryRes.status === 'fulfilled' && summaryRes.value.data?.success) {
      setSummary(summaryRes.value.data.data);
    }

    if (usersRes.status === 'fulfilled' && usersRes.value.data?.success) {
      const loadedUsers = usersRes.value.data.data || [];
      setUsers(loadedUsers);
      setRecoveryEmailByUser((prev) => {
        const next = { ...prev };
        loadedUsers.forEach((user) => {
          if (next[user._id] === undefined) {
            next[user._id] = user.recoveryEmail || '';
          }
        });
        return next;
      });
    } else {
      setUsers([]);
      const usersError =
        usersRes.status === 'rejected'
          ? usersRes.reason?.response?.data?.message
          : usersRes.value?.data?.message;
      setError(usersError || 'Failed to load users');
    }

    if (logsRes.status === 'fulfilled' && logsRes.value.data?.success) {
      setLogs(logsRes.value.data.data || []);
    } else {
      setLogs([]);
    }

    if (groupsRes.status === 'fulfilled' && groupsRes.value.data?.success) {
      setGroups(groupsRes.value.data.data || []);
    } else {
      setGroups([]);
    }

    setFeedbackItems(
      feedbackRes.status === 'fulfilled' && feedbackRes.value.data?.success
        ? feedbackRes.value.data.data || []
        : []
    );

    setLoading(false);
  };

  useEffect(() => {
    loadAdminData('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadAdminData(query);
  };

  const updateUserStatus = async (user, isActive) => {
    setMessage('');
    setError('');
    try {
      const res = await axiosInstance.patch(`/api/admin/users/${user._id}/status`, { isActive });
      if (res.data.success) {
        setUsers((prev) => prev.map((u) => (u._id === user._id ? res.data.data : u)));
        setMessage(`${user.email} ${isActive ? 'activated' : 'disabled'}`);
        loadAdminData(query);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const resetPassword = async (user) => {
    const password = newPasswordByUser[user._id] || '';
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setMessage('');
    setError('');
    try {
      const res = await axiosInstance.patch(`/api/admin/users/${user._id}/password`, { password });
      if (res.data.success) {
        setNewPasswordByUser((prev) => ({ ...prev, [user._id]: '' }));
        setMessage(`Password reset for ${user.email}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to reset password');
    }
  };

  const saveRecoveryEmail = async (user) => {
    const recoveryEmail = (recoveryEmailByUser[user._id] || '').trim().toLowerCase();
    setMessage('');
    setError('');
    try {
      const res = await axiosInstance.patch(`/api/admin/users/${user._id}/recovery-email`, {
        recoveryEmail,
      });
      if (res.data.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u._id === user._id ? { ...u, recoveryEmail: res.data.data.user.recoveryEmail } : u
          )
        );
        setMessage(`Recovery email saved for ${user.email}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save recovery email');
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.email} from app database? Maildir will remain on server.`)) return;

    setMessage('');
    setError('');
    try {
      const res = await axiosInstance.delete(`/api/admin/users/${user._id}`);
      if (res.data.success) {
        setUsers((prev) => prev.filter((u) => u._id !== user._id));
        setMessage(`${user.email} deleted from app database`);
        loadAdminData(query);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  useEffect(() => {
    const q = groupMemberInput.trim();
    if (!editingGroup || q.length < 1) {
      setGroupSuggestions([]);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const res = await axiosInstance.get('/api/users/suggestions', { params: { q } });
        if (res.data.success) {
          setGroupSuggestions(
            (res.data.data || []).filter((u) => !groupMembers.includes(normalizeEmail(u.email)))
          );
        }
      } catch (err) {
        setGroupSuggestions([]);
      }
    }, 180);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMemberInput, editingGroup, groupMembers]);

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupName(group.name || '');
    setGroupMembers((group.members || []).map((m) => normalizeEmail(m.email)));
    setGroupMemberInput('');
    setGroupSuggestions([]);
    setShowGroupSuggestions(false);
    setGroupModalError('');
  };

  const closeGroupModal = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupMembers([]);
    setGroupMemberInput('');
    setGroupSuggestions([]);
    setShowGroupSuggestions(false);
    setGroupModalError('');
  };

  const addGroupMember = (rawEmail) => {
    const email = normalizeEmail(rawEmail).replace(/,$/, '');
    if (!email) return;
    if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(email)) {
      setGroupModalError('Only @atozasmail.in users can be added.');
      return;
    }
    if (groupMembers.includes(email)) {
      setGroupMemberInput('');
      return;
    }
    setGroupMembers((prev) => [...prev, email]);
    setGroupMemberInput('');
    setGroupSuggestions([]);
    setShowGroupSuggestions(false);
    setGroupModalError('');
  };

  const removeGroupMember = (email) => {
    if (normalizeEmail(email) === creatorEmail) return;
    setGroupMembers((prev) => prev.filter((item) => item !== email));
  };

  const handleGroupMemberKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addGroupMember(groupMemberInput);
    }
    if (event.key === 'Backspace' && !groupMemberInput && groupMembers.length > 0) {
      const last = groupMembers[groupMembers.length - 1];
      if (normalizeEmail(last) !== creatorEmail) {
        setGroupMembers((prev) => prev.slice(0, -1));
      }
    }
  };

  const handleSaveGroup = async () => {
    if (!editingGroup) return;
    const name = groupName.trim();
    if (!name) {
      setGroupModalError('Group name is required.');
      return;
    }

    let members = [...groupMembers];
    const pending = normalizeEmail(groupMemberInput).replace(/,$/, '');
    if (pending) {
      if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(pending)) {
        setGroupModalError('Only @atozasmail.in users can be added.');
        return;
      }
      if (!members.includes(pending)) members = [...members, pending];
    }

    setGroupModalError('');
    setSavingGroup(true);
    try {
      const res = await axiosInstance.patch(`/api/admin/groups/${editingGroup._id}`, {
        name,
        members,
      });
      if (res.data.success) {
        setGroups((prev) => prev.map((g) => (g._id === editingGroup._id ? res.data.data : g)));
        setMessage(res.data.message || 'Group updated');
        closeGroupModal();
      } else {
        setGroupModalError(res.data.message || 'Could not update group');
      }
    } catch (err) {
      setGroupModalError(err.response?.data?.message || 'Could not update group');
    } finally {
      setSavingGroup(false);
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"? All its messages will be removed. This cannot be undone.`)) return;

    setMessage('');
    setError('');
    try {
      const res = await axiosInstance.delete(`/api/admin/groups/${group._id}`);
      if (res.data.success) {
        setGroups((prev) => prev.filter((g) => g._id !== group._id));
        setMessage(`Group "${group.name}" deleted`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete group');
    }
  };

  if (loading) {
    return <div className="admin-panel"><div className="admin-card">Loading admin panel...</div></div>;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h2>Admin Panel</h2>
          <p>Signed in as {currentUser?.email}</p>
        </div>
        <div className="admin-header-actions">
          <a className="admin-refresh-btn" href="#ai-settings">AI Settings</a>
          <a className="admin-refresh-btn" href="#docs-templates">Docs templates</a>
          <button type="button" className="admin-refresh-btn" onClick={() => loadAdminData(query)}>Refresh</button>
        </div>
      </div>

      {message && <div className="admin-alert success">{message}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      <div className="admin-grid">
        <div className="admin-stat-card">
          <span>Total Users</span>
          <strong>{summary?.totalUsers ?? 0}</strong>
        </div>
        <div className="admin-stat-card">
          <span>Active Users</span>
          <strong>{summary?.activeUsers ?? 0}</strong>
        </div>
        <div className="admin-stat-card">
          <span>Disabled Users</span>
          <strong>{summary?.disabledUsers ?? 0}</strong>
        </div>
        <div className="admin-stat-card">
          <span>Sent Mail Docs</span>
          <strong>{summary?.sentMails ?? 0}</strong>
        </div>
      </div>

      <AiSettingsAdmin />

      <div id="docs-templates">
        <DocTemplatesAdmin />
      </div>

      <section className="admin-card admin-users-card">
        <div className="admin-section-title">
          <h3>
            Users
            <span className="admin-user-count">({users.length})</span>
          </h3>
          <form onSubmit={handleSearch} className="admin-search">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search users" />
            <button type="submit">Search</button>
          </form>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Created</th>
                <th>Password Reset</th>
                <th>Recovery email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <strong>{user.username}</strong>
                    <span>{user.email}</span>
                  </td>
                  <td>{user.isActive ? 'Active' : 'Disabled'}</td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</td>
                  <td>
                    <div className="admin-password-row">
                      <input
                        type="password"
                        value={newPasswordByUser[user._id] || ''}
                        onChange={(e) => setNewPasswordByUser((prev) => ({ ...prev, [user._id]: e.target.value }))}
                        placeholder="New password"
                      />
                      <button onClick={() => resetPassword(user)}>Reset</button>
                    </div>
                  </td>
                  <td>
                    <div className="admin-password-row">
                      <input
                        type="email"
                        value={recoveryEmailByUser[user._id] || ''}
                        onChange={(e) =>
                          setRecoveryEmailByUser((prev) => ({
                            ...prev,
                            [user._id]: e.target.value,
                          }))
                        }
                        placeholder="you@gmail.com"
                      />
                      <button onClick={() => saveRecoveryEmail(user)}>Save</button>
                    </div>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button onClick={() => updateUserStatus(user, !user.isActive)}>
                        {user.isActive ? 'Disable' : 'Activate'}
                      </button>
                      <button className="danger" onClick={() => deleteUser(user)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr>
                  <td colSpan="6" className="admin-empty">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-card admin-groups-card">
        <div className="admin-section-title">
          <h3>
            Mail Groups
            <span className="admin-user-count">({groups.length})</span>
          </h3>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Group</th>
                <th>Created by</th>
                <th>Members</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group._id}>
                  <td>
                    <strong>{group.name}</strong>
                  </td>
                  <td>
                    <span>{group.createdBy?.username || '-'}</span>
                    <span className="admin-group-email">{group.createdBy?.email || ''}</span>
                  </td>
                  <td>
                    <span className="admin-group-members" title={(group.members || []).map((m) => m.email).join(', ')}>
                      {group.members?.length || 0} member{(group.members?.length || 0) === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td>{group.createdAt ? new Date(group.createdAt).toLocaleDateString() : '-'}</td>
                  <td>
                    <div className="admin-actions">
                      <button onClick={() => openEditGroup(group)}>Edit</button>
                      <button className="danger" onClick={() => deleteGroup(group)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!groups.length && (
                <tr>
                  <td colSpan="5" className="admin-empty">No groups created yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-card admin-feedback-card">
        <div className="admin-section-title">
          <h3>Feedback <span className="admin-user-count">({feedbackItems.length})</span></h3>
        </div>
        <div className="admin-feedback-list">
          {feedbackItems.map((item) => (
            <article className="admin-feedback-item" key={item._id}>
              <div><strong>{item.email}</strong><time>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</time></div>
              <p>{item.message}</p>
            </article>
          ))}
          {!feedbackItems.length && <p className="admin-empty">No feedback submitted yet.</p>}
        </div>
      </section>

      {editingGroup && (
        <div className="admin-group-overlay" role="presentation" onClick={closeGroupModal}>
          <div
            className="admin-group-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-group-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-group-modal-head">
              <h3 id="admin-group-title">Edit group</h3>
              <button type="button" className="admin-group-close" onClick={closeGroupModal} aria-label="Close">x</button>
            </div>

            <label className="admin-group-field">
              <span>Group name</span>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                autoFocus
              />
            </label>

            <div className="admin-group-field admin-group-members-field">
              <span>Members</span>
              <small className="admin-group-hint">
                The group owner ({creatorEmail}) is always a member and cannot be removed.
              </small>
              <div className="admin-group-input-wrap">
                {groupMembers.map((email) => (
                  <span
                    className={`admin-group-chip ${normalizeEmail(email) === creatorEmail ? 'is-owner' : ''}`}
                    key={email}
                  >
                    {email}
                    {normalizeEmail(email) !== creatorEmail && (
                      <button type="button" onClick={() => removeGroupMember(email)} aria-label={`Remove ${email}`}>x</button>
                    )}
                  </span>
                ))}
                <input
                  type="email"
                  value={groupMemberInput}
                  onChange={(e) => {
                    setGroupMemberInput(e.target.value);
                    setShowGroupSuggestions(true);
                  }}
                  onKeyDown={handleGroupMemberKeyDown}
                  onFocus={() => setShowGroupSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowGroupSuggestions(false), 120)}
                  placeholder="user@atozasmail.in"
                  autoComplete="off"
                />
              </div>
              {showGroupSuggestions && groupSuggestions.length > 0 && (
                <div className="admin-group-suggestions">
                  {groupSuggestions.map((user) => (
                    <button
                      type="button"
                      className="admin-group-suggestion"
                      key={user._id || user.email}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        addGroupMember(user.email);
                      }}
                    >
                      <strong>{user.username || user.email}</strong>
                      <small>{user.email}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {groupModalError && <div className="admin-alert error">{groupModalError}</div>}

            <div className="admin-group-actions">
              <button type="button" className="admin-group-cancel" onClick={closeGroupModal}>Cancel</button>
              <button
                type="button"
                className="admin-group-save"
                onClick={handleSaveGroup}
                disabled={savingGroup || !groupName.trim()}
              >
                {savingGroup ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="admin-card admin-logs-card">
        <div className="admin-section-title">
          <h3>Recent Logs</h3>
          <button onClick={() => loadAdminData(query)}>Reload Logs</button>
        </div>
        <div className="admin-logs">
          {logs.map((log) => (
            <div key={log.path} className="admin-log-block">
              <h4>{log.path}</h4>
              <pre>{(log.lines || []).join('\n')}</pre>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminPanel;
